import { useCallback, useEffect, useMemo, useState } from "react";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import axios from "../../../axios";
import * as S from "../Funnel/Funnel.styled";
import * as C from "./Content.styled";

// «Контент»: Instagram/TikTok — наші пости, конкуренти, банк ідей і звіти агента-контент-стратега.
// Дані збирає marketing/content-agent/collect.py, аналізує агент за розкладом (див. marketing/content-agent/README.md).

const TARGET_PER_WEEK = 4;
const PLATFORM_LABEL = { instagram: "Instagram", tiktok: "TikTok" };
const STATUS = {
  new: { label: "Нові", tag: "brand" },
  accepted: { label: "Беру", tag: "amber" },
  filmed: { label: "Знято", tag: "pink" },
  published: { label: "Опубліковано", tag: "green" },
  rejected: { label: "Відхилено", tag: "red" },
};
const VERDICT = { hit: ["🔥 хіт", "green"], ok: ["норм", "brand"], flop: ["провал", "red"] };

const num = (n) => (n == null ? "—"
  : n >= 1e6 ? `${(n / 1e6).toFixed(1).replace(/\.0$/, "")}M`
  : n >= 1e4 ? `${Math.round(n / 1000)}k`
  : n.toLocaleString("uk-UA"));
const day = (d) => (d ? new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" }) : "—");
const when = (d) => (d ? new Date(d).toLocaleString("uk-UA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const signed = (n) => (n == null ? "" : n > 0 ? `+${n}` : String(n));
const errText = (e, fallback) => e?.response?.data?.message || fallback;

// Мінімальний markdown для звітів агента: заголовки, списки, **жирний**, посилання. Спершу екрануємо HTML.
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const inline = (s) => esc(s)
  .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
  .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
  .replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');
const mdToHtml = (md = "") => {
  const out = [];
  let list = false;
  for (const raw of md.split("\n")) {
    const line = raw.trimEnd();
    const li = line.match(/^\s*[-*•]\s+(.*)/);
    if (li) { if (!list) { out.push("<ul>"); list = true; } out.push(`<li>${inline(li[1])}</li>`); continue; }
    if (list) { out.push("</ul>"); list = false; }
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) out.push(`<h4>${inline(h[2])}</h4>`);
    else if (line.trim()) out.push(`<p>${inline(line)}</p>`);
  }
  if (list) out.push("</ul>");
  return out.join("");
};
const Markdown = ({ text }) => <C.Md dangerouslySetInnerHTML={{ __html: mdToHtml(text) }} />;

const RatioTag = ({ v }) => (v == null ? null : <C.Ratio $v={v} title="У скільки разів більше/менше звичайних переглядів цього акаунта">×{v}</C.Ratio>);

const PostCards = ({ posts, showOwner }) => (
  <C.Cards>
    {posts.map((p) => (
      <C.PostCard key={p._id || p.postId} href={p.url} target="_blank" rel="noopener noreferrer">
        <div className="top">
          <span>{showOwner ? `@${p.owner}` : p.format} · {day(p.publishedAt)}</span>
          <RatioTag v={p.ratio} />
        </div>
        <div className="cap">{p.caption || "без підпису"}</div>
        <div className="nums">
          <span>👁 <b>{num(p.views)}</b></span>
          <span>❤ {num(p.likes)}</span>
          <span>💬 {num(p.comments)}</span>
          {p.shares != null && <span>↗ {num(p.shares)}</span>}
          {p.duration ? <span>{Math.round(p.duration)} с</span> : null}
        </div>
      </C.PostCard>
    ))}
  </C.Cards>
);

function Overview({ platform, go }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    axios.get("admin/content/overview", { params: { platform } })
      .then((r) => setData(r.data)).catch((e) => setError(errText(e, "Не вдалося завантажити огляд")));
  }, [platform]);
  if (error) return <S.ErrorText>{error}</S.ErrorText>;
  if (!data) return <S.Empty>Завантажую…</S.Empty>;
  const { kpi, weeks, hits, topOwn, lastDaily, ideas } = data;
  if (!data.profile) return <S.Card><S.Empty>Ще немає даних {PLATFORM_LABEL[platform]}. Збирач запускається щоранку.</S.Empty></S.Card>;
  const maxPosts = Math.max(TARGET_PER_WEEK + 1, ...weeks.map((w) => w.posts));

  return (
    <>
      <C.Kpis>
        <C.Kpi><span>Підписники @{data.profile.username}</span><b>{num(kpi.followers)}</b><em>{kpi.followers7 != null ? `${signed(kpi.followers7)} за 7 днів` : "динаміка — з наступних знімків"}</em></C.Kpi>
        <C.Kpi $tone={kpi.postsPerWeek < TARGET_PER_WEEK - 1 ? "bad" : "good"}><span>Публікацій на тиждень</span><b>{kpi.postsPerWeek}</b><em>ціль — {TARGET_PER_WEEK}+ · за 30 днів: {kpi.posts30}</em></C.Kpi>
        <C.Kpi><span>Звичайні перегляди (медіана)</span><b>{num(kpi.medianViews)}</b><em>за останні 60 днів</em></C.Kpi>
        <C.Kpi $tone={kpi.leads30 ? "good" : undefined}><span>Заявки з {PLATFORM_LABEL[platform]} за 30 днів</span><b>{kpi.leads30}</b><em>з них стали учнями: {kpi.paid30}</em></C.Kpi>
      </C.Kpis>

      {lastDaily && (
        <S.Card>
          <S.CardHead>
            <div><S.H>🤖 Агент сьогодні</S.H><S.Hint>{lastDaily.title} · {when(lastDaily.createdAt)}</S.Hint></div>
            <S.Btn $ghost onClick={() => go("reports")}>Усі звіти</S.Btn>
          </S.CardHead>
          <Markdown text={lastDaily.summary || lastDaily.body} />
        </S.Card>
      )}

      <S.Row2>
        <S.Card>
          <S.CardHead><div><S.H>Публікації по тижнях</S.H><S.Hint>пунктир — ціль {TARGET_PER_WEEK} на тиждень · під стовпчиком заявки</S.Hint></div></S.CardHead>
          <C.Weeks>
            {weeks.map((w) => (
              <C.Week key={w.start} $low={w.posts < TARGET_PER_WEEK}>
                <b>{w.posts}</b>
                <div className="bar">
                  <i style={{ height: `${(w.posts / maxPosts) * 100}%` }} />
                  <s style={{ bottom: `${(TARGET_PER_WEEK / maxPosts) * 100}%` }} />
                </div>
                <span>{day(w.start)}</span>
                <span title="заявки / учні">📩 {w.leads}{w.paid ? ` · ${w.paid}✓` : ""}</span>
              </C.Week>
            ))}
          </C.Weeks>
        </S.Card>
        <S.Card>
          <S.CardHead>
            <div><S.H>Банк ідей</S.H><S.Hint>ідеї агента і ваші — беріть у роботу</S.Hint></div>
            <S.Btn onClick={() => go("ideas")}>Відкрити</S.Btn>
          </S.CardHead>
          <S.Stats>
            {Object.entries(STATUS).filter(([k]) => k !== "rejected").map(([k, s]) => (
              <S.Stat key={k}><S.Big>{ideas[k] || 0}</S.Big><S.Small>{s.label}</S.Small></S.Stat>
            ))}
          </S.Stats>
        </S.Card>
      </S.Row2>

      <S.Card>
        <S.CardHead>
          <div><S.H>🔥 Що зараз «вистрілило» в конкурентів</S.H><S.Hint>пости за 14 днів, які набрали в 2,5+ рази більше, ніж зазвичай у цього акаунта — перевірені теми</S.Hint></div>
          <S.Btn $ghost onClick={() => go("competitors")}>Усі конкуренти</S.Btn>
        </S.CardHead>
        {hits.length ? <PostCards posts={hits} showOwner /> : <S.Empty>Поки немає явних хітів.</S.Empty>}
      </S.Card>

      <S.Card>
        <S.CardHead><S.H>Наші найкращі пости</S.H><S.Btn $ghost onClick={() => go("own")}>Усі наші пости</S.Btn></S.CardHead>
        {topOwn.length ? <PostCards posts={topOwn} /> : <S.Empty>Немає постів за 90 днів.</S.Empty>}
      </S.Card>
    </>
  );
}

function Posts({ platform, scope }) {
  const [data, setData] = useState(null);
  const [owner, setOwner] = useState("");
  const [hitsOnly, setHitsOnly] = useState(false);
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");
  const apiScope = scope === "own" ? "own" : hitsOnly ? "hits" : scope;

  useEffect(() => {
    setData(null);
    axios.get("admin/content/posts", { params: { platform, scope: apiScope, days } })
      .then((r) => setData(r.data)).catch((e) => setError(errText(e, "Не вдалося завантажити пости")));
  }, [platform, apiScope, days]);

  const posts = useMemo(() => (data?.posts || []).filter((p) => !owner || p.owner === owner), [data, owner]);
  const owners = useMemo(() => [...new Set((data?.posts || []).map((p) => p.owner))].sort(), [data]);
  const profile = (u) => data?.profiles.find((p) => p.username === u);

  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>{scope === "own" ? "Наші пости" : scope === "discover" ? "Знайдено за хештегами" : "Конкуренти"}</S.H>
          <S.Hint>«×» — у скільки разів більше чи менше звичайних переглядів акаунта (медіана за 60 днів)</S.Hint>
        </div>
        <C.Tabs>
          {scope === "competitors" && (
            <>
              <S.Select value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">Усі акаунти</option>
                {owners.map((o) => <option key={o} value={o}>@{o} {profile(o)?.followers ? `· ${num(profile(o).followers)}` : ""}</option>)}
              </S.Select>
              <C.TabBtn $active={hitsOnly} onClick={() => setHitsOnly((v) => !v)}>Тільки хіти</C.TabBtn>
            </>
          )}
          <S.Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} днів</option>)}
          </S.Select>
        </C.Tabs>
      </S.CardHead>
      {error && <S.ErrorText>{error}</S.ErrorText>}
      {!data ? <S.Empty>Завантажую…</S.Empty> : !posts.length ? <S.Empty>Немає постів за цей період.</S.Empty> : (
        <S.Table>
          <table>
            <thead>
              <tr>
                <th>Дата</th>{scope !== "own" && <th>Акаунт</th>}<th>Формат</th><th>Підпис</th>
                <th>Перегляди</th><th>×</th><th>❤</th><th>💬</th>{platform === "tiktok" && <th>↗</th>}<th>Тривалість</th>
                {scope === "own" && <th>Ідея</th>}
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p._id}>
                  <td>{day(p.publishedAt)}</td>
                  {scope !== "own" && <td>@{p.owner}</td>}
                  <td>{p.format}{p.isPinned ? " 📌" : ""}</td>
                  <td><a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}><C.Cap>{p.caption || "—"}</C.Cap></a></td>
                  <td><b>{num(p.views)}</b></td>
                  <td><RatioTag v={p.ratio} /></td>
                  <td>{num(p.likes)}</td>
                  <td>{num(p.comments)}</td>
                  {platform === "tiktok" && <td>{num(p.shares)}</td>}
                  <td>{p.duration ? `${Math.round(p.duration)} с` : "—"}</td>
                  {scope === "own" && <td>{p.idea ? <S.Tag>{p.idea.title}</S.Tag> : ""}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </S.Table>
      )}
    </S.Card>
  );
}

// Приблизна вартість Apify на місяць: конкурент Instagram ~$0.2 (пн+чт), наш акаунт ~$0.3, хештеги ~$1.2. TikTok — безкоштовно.
const apifyMonthly = (igCompetitors) => Math.round((igCompetitors * 0.2 + 0.3 + 1.2) * 10) / 10;
const GROUP_LABEL = { "ua-school": "школа UA", "ua-creator": "автор UA", intl: "зарубіжний", own: "ми" };

function Accounts({ platform }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [group, setGroup] = useState("ua-school");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    axios.get("admin/content/accounts").then((r) => setData(r.data)).catch((e) => setMsg(errText(e, "Не вдалося завантажити список")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      const { data: d } = await axios.post("admin/content/accounts", { link, platform, group, note });
      setLink(""); setNote("");
      setMsg(`✅ Додано @${d.account.username} (${PLATFORM_LABEL[d.account.platform]}). Дані з'являться після найближчого збору.`);
      load();
    } catch (err) { setMsg("⚠️ " + errText(err, "Не вдалося додати")); }
  };
  const toggle = async (a) => {
    if (a.active && !window.confirm(`Перестати стежити за @${a.username}?`)) return;
    await axios.patch(`admin/content/accounts/${a._id}`, { active: !a.active }).catch(() => {});
    load();
  };

  const list = (data?.accounts || []).filter((a) => a.platform === platform && !a.isOwn);
  const active = list.filter((a) => a.active);
  const igCount = (data?.accounts || []).filter((a) => a.platform === "instagram" && !a.isOwn && a.active).length;

  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>За ким стежимо в {PLATFORM_LABEL[platform]}: {active.length}</S.H>
          <S.Hint>
            Агент враховує всіх зі списку.{" "}
            {platform === "instagram"
              ? <>Instagram збирається через Apify: ≈ ${apifyMonthly(igCount)}/міс (безкоштовно до $5).</>
              : <>TikTok збирається безкоштовно.</>}
          </S.Hint>
        </div>
        <S.Btn $ghost onClick={() => setOpen((v) => !v)}>{open ? "Згорнути" : "Керувати списком"}</S.Btn>
      </S.CardHead>
      {open && (
        <>
          <S.Form onSubmit={add}>
            <label style={{ gridColumn: "span 2" }}>Посилання або @нік
              <S.Input required value={link} onChange={(e) => setLink(e.target.value)} placeholder={platform === "instagram" ? "https://www.instagram.com/…" : "https://www.tiktok.com/@…"} />
            </label>
            <label>Тип
              <S.Select value={group} onChange={(e) => setGroup(e.target.value)}>
                {(data?.groups || []).map((g) => <option key={g} value={g}>{GROUP_LABEL[g] || g}</option>)}
              </S.Select>
            </label>
            <label>Нотатка
              <S.Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="чим цікавий" />
            </label>
            <S.Btn type="submit">Додати</S.Btn>
          </S.Form>
          {msg && <S.Hint style={{ fontWeight: 700, marginBottom: 10 }}>{msg}</S.Hint>}
          {platform === "instagram" && igCount >= 22 && (
            <S.Hint style={{ color: "#D6455D", fontWeight: 700 }}>Список великий: вартість Apify перевищить безкоштовні $5. Варто прибрати слабкі акаунти або перейти на платний тариф.</S.Hint>
          )}
          <S.List>
            {list.map((a) => (
              <li key={a._id} style={{ opacity: a.active ? 1 : 0.5 }}>
                <span>
                  <a href={platform === "instagram" ? `https://www.instagram.com/${a.username}/` : `https://www.tiktok.com/@${a.username}`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", fontWeight: 700 }}>@{a.username}</a>
                  {" "}<S.Tag>{GROUP_LABEL[a.group] || a.group}</S.Tag>
                  {a.followers ? <S.Small as="span"> · {num(a.followers)} підписників</S.Small> : null}
                  {a.note ? <S.Hint style={{ margin: 0 }}>{a.note}</S.Hint> : null}
                </span>
                <S.Btn $ghost onClick={() => toggle(a)}>{a.active ? "Прибрати" : "Повернути"}</S.Btn>
              </li>
            ))}
          </S.List>
        </>
      )}
    </S.Card>
  );
}

function IdeaCard({ idea, onChange }) {
  const [busy, setBusy] = useState(false);
  const patch = async (body) => {
    setBusy(true);
    try { const { data } = await axios.patch(`admin/content/ideas/${idea._id}`, body); onChange(data.idea); }
    catch (e) { alert(errText(e, "Не вдалося зберегти")); }
    finally { setBusy(false); }
  };
  const reject = () => { const r = window.prompt("Чому не підходить? (агент врахує)"); if (r !== null) patch({ status: "rejected", rejectReason: r }); };
  const publish = () => { const u = window.prompt("Посилання на опублікований рілс/відео:", idea.publishedUrl || ""); if (u) patch({ status: "published", publishedUrl: u.trim() }); };
  const comment = () => { const c = window.prompt("Коментар для агента / команди:", idea.comment || ""); if (c !== null) patch({ comment: c }); };
  const verdict = idea.result?.verdict && VERDICT[idea.result.verdict];

  return (
    <C.Idea $p={idea.priority}>
      <div className="meta">
        <S.Tag $c={STATUS[idea.status].tag}>{STATUS[idea.status].label}</S.Tag>
        {idea.priority === 1 && <S.Tag $c="pink">гаряче</S.Tag>}
        {idea.format && <S.Tag>{idea.format}</S.Tag>}
        {idea.series && <S.Tag>серія: {idea.series}</S.Tag>}
        {(idea.platforms || []).map((p) => <S.Tag key={p} $c="amber">{PLATFORM_LABEL[p] || p}</S.Tag>)}
        <S.Small>{day(idea.createdAt)} · {idea.createdBy === "agent" ? "агент" : "команда"}</S.Small>
      </div>
      <h4>{idea.title}</h4>
      {idea.hook && <div className="hook"><b>Хук:</b> {idea.hook}</div>}
      {idea.rationale && <div className="why">{idea.rationale}</div>}
      {idea.script && <details><summary>Сценарій</summary><div>{idea.script}</div></details>}
      {idea.cta && <S.Small><b>CTA:</b> {idea.cta}</S.Small>}
      {!!idea.references?.length && (
        <S.Small>Референси: {idea.references.map((r, i) => (
          <span key={i}>{i ? ", " : ""}<a href={r.url} target="_blank" rel="noopener noreferrer">@{r.owner || "пост"}{r.views ? ` (${num(r.views)})` : ""}</a></span>
        ))}</S.Small>
      )}
      {idea.publishedUrl && <S.Small>Опубліковано: <a href={idea.publishedUrl} target="_blank" rel="noopener noreferrer">відкрити</a></S.Small>}
      {verdict && <S.Small><S.Tag $c={verdict[1]}>{verdict[0]}</S.Tag> {num(idea.result.views)} переглядів{idea.result.vsMedian ? ` · ×${idea.result.vsMedian} до норми` : ""}{idea.result.note ? ` — ${idea.result.note}` : ""}</S.Small>}
      {idea.rejectReason && <S.Small>Причина відмови: {idea.rejectReason}</S.Small>}
      {idea.comment && <S.Small>💬 {idea.comment}</S.Small>}
      <div className="actions">
        {idea.status === "new" && <S.Btn disabled={busy} onClick={() => patch({ status: "accepted" })}>Беру</S.Btn>}
        {idea.status === "accepted" && <S.Btn disabled={busy} onClick={() => patch({ status: "filmed" })}>Знято</S.Btn>}
        {["accepted", "filmed"].includes(idea.status) && <S.Btn disabled={busy} onClick={publish}>Опубліковано</S.Btn>}
        {idea.status === "published" && <S.Btn $ghost disabled={busy} onClick={publish}>Змінити посилання</S.Btn>}
        {["new", "accepted"].includes(idea.status) && <S.Btn $ghost disabled={busy} onClick={reject}>Не підходить</S.Btn>}
        {idea.status === "rejected" && <S.Btn $ghost disabled={busy} onClick={() => patch({ status: "new" })}>Повернути</S.Btn>}
        <S.Btn $ghost disabled={busy} onClick={comment}>Коментар</S.Btn>
      </div>
    </C.Idea>
  );
}

function Ideas() {
  const [ideas, setIdeas] = useState(null);
  const [filter, setFilter] = useState("active");
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios.get("admin/content/ideas").then((r) => setIdeas(r.data.ideas)).catch((e) => setError(errText(e, "Не вдалося завантажити ідеї")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => (ideas || []).reduce((a, i) => ({ ...a, [i.status]: (a[i.status] || 0) + 1 }), {}), [ideas]);
  const shown = (ideas || []).filter((i) => (filter === "active" ? ["new", "accepted", "filmed"].includes(i.status) : i.status === filter));
  const replace = (idea) => setIdeas((list) => list.map((i) => (i._id === idea._id ? idea : i)));
  const save = async (e) => {
    e.preventDefault();
    try { await axios.post("admin/content/ideas", form); setForm(null); load(); }
    catch (err) { alert(errText(err, "Не вдалося додати")); }
  };

  return (
    <S.Card>
      <S.CardHead>
        <C.Tabs>
          <C.TabBtn $active={filter === "active"} onClick={() => setFilter("active")}>У роботі<small>{(counts.new || 0) + (counts.accepted || 0) + (counts.filmed || 0)}</small></C.TabBtn>
          {Object.entries(STATUS).map(([k, s]) => (
            <C.TabBtn key={k} $active={filter === k} onClick={() => setFilter(k)}>{s.label}<small>{counts[k] || 0}</small></C.TabBtn>
          ))}
        </C.Tabs>
        <S.Btn onClick={() => setForm(form ? null : { title: "", hook: "", script: "", platforms: ["instagram", "tiktok"] })}>{form ? "Скасувати" : "+ Своя ідея"}</S.Btn>
      </S.CardHead>
      {form && (
        <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          <S.Input required placeholder="Ідея (коротко)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <S.Input placeholder="Хук — перша фраза / текст на екрані" value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} />
          <C.TextArea rows={4} placeholder="Сценарій / нотатки" value={form.script} onChange={(e) => setForm({ ...form, script: e.target.value })} />
          <div><S.Btn type="submit">Додати</S.Btn></div>
        </form>
      )}
      {error && <S.ErrorText>{error}</S.ErrorText>}
      {!ideas ? <S.Empty>Завантажую…</S.Empty> : !shown.length ? <S.Empty>Тут порожньо.</S.Empty> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {shown.map((i) => <IdeaCard key={i._id} idea={i} onChange={replace} />)}
        </div>
      )}
    </S.Card>
  );
}

function Reports() {
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    axios.get("admin/content/reports").then((r) => {
      setReports(r.data.reports);
      setOpen(r.data.reports[0]?._id || null);
    }).catch((e) => setError(errText(e, "Не вдалося завантажити звіти")));
  }, []);
  const KIND = { daily: ["день", "brand"], weekly: ["тиждень", "amber"], playbook: ["плейбук", "green"] };

  if (error) return <S.ErrorText>{error}</S.ErrorText>;
  if (!reports) return <S.Empty>Завантажую…</S.Empty>;
  if (!reports.length) return <S.Card><S.Empty>Агент ще не написав жодного звіту.</S.Empty></S.Card>;
  return reports.map((r) => (
    <S.Card key={r._id}>
      <S.CardHead style={{ cursor: "pointer", marginBottom: open === r._id ? 14 : 0 }} onClick={() => setOpen(open === r._id ? null : r._id)}>
        <div>
          <S.H><S.Tag $c={KIND[r.kind][1]}>{KIND[r.kind][0]}</S.Tag> {r.title}</S.H>
          <S.Hint>{when(r.createdAt)}{open !== r._id && r.summary ? ` — ${r.summary.slice(0, 140)}${r.summary.length > 140 ? "…" : ""}` : ""}</S.Hint>
        </div>
        <span>{open === r._id ? "▴" : "▾"}</span>
      </S.CardHead>
      {open === r._id && <Markdown text={r.body || r.summary} />}
    </S.Card>
  ));
}

const TABS = [
  ["overview", "Огляд"],
  ["own", "Наші пости"],
  ["competitors", "Конкуренти"],
  ["ideas", "Банк ідей"],
  ["reports", "Звіти агента"],
  ["discover", "Хештеги"],
];

function Content() {
  const [tab, setTab] = useState(() => { try { return localStorage.getItem("content.tab") || "overview"; } catch { return "overview"; } });
  const [platform, setPlatform] = useState(() => { try { return localStorage.getItem("content.platform") || "instagram"; } catch { return "instagram"; } });
  const go = (t) => { setTab(t); try { localStorage.setItem("content.tab", t); } catch { /* приватний режим */ } };
  const pick = (p) => { setPlatform(p); try { localStorage.setItem("content.platform", p); } catch { /* приватний режим */ } };
  const perPlatform = !["ideas", "reports"].includes(tab);

  return (
    <PagesWrapper>
      <TopPageTitle value={"Контент"} />
      <S.Page>
        <C.Tabs>
          {TABS.filter(([k]) => k !== "discover" || platform === "instagram").map(([k, label]) => (
            <C.TabBtn key={k} $active={tab === k} onClick={() => go(k)}>{label}</C.TabBtn>
          ))}
          <C.Spacer />
          {perPlatform && Object.entries(PLATFORM_LABEL).map(([k, label]) => (
            <C.TabBtn key={k} $active={platform === k} onClick={() => pick(k)}>{label}</C.TabBtn>
          ))}
        </C.Tabs>
        {tab === "overview" && <Overview platform={platform} go={go} />}
        {tab === "competitors" && <Accounts key={`acc-${platform}`} platform={platform} />}
        {["own", "competitors"].includes(tab) && <Posts key={`${tab}-${platform}`} platform={platform} scope={tab} />}
        {tab === "discover" && <Posts key="discover" platform="instagram" scope="discover" />}
        {tab === "ideas" && <Ideas />}
        {tab === "reports" && <Reports />}
      </S.Page>
    </PagesWrapper>
  );
}

export default Content;
